import { Button, Card, CenteredSpinner, Col, Row, Title } from '@wyz/ui';
import { useMutationState } from '@wyz/utils';
import { useFormContext, useWatch } from 'react-hook-form';
import { CheckboxField, SelectField, TextField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { PurchaseQueryKeys, useManageQuoteSetting } from '~/purchase/api';
import { QuoteContactForm } from '~/purchase/components';
import { usePurchaseContext } from '~/purchase/context';
import { IQuoteCartForm } from '~/purchase/types';
const SAVE_AND_SEND_BUTTON_ID = 'save-and-send';
const PRINT_BUTTON_ID = 'print';

const QuoteCartContacts: React.FC = () => {
  const { t } = useI18n();
  const { state } = usePurchaseContext();

  const mutationState = useMutationState({
    filters: {
      mutationKey: PurchaseQueryKeys.quoteCart.submit(
        state.primaryCustomer?.id as string,
      ),
    },
  });
  const quoteSettingManagement = useManageQuoteSetting({
    get: { enabled: true },
  });
  const methods = useFormContext<IQuoteCartForm>();

  const [watchedContactMail, watchedGdprAccepted] = useWatch({
    name: ['contact.email', 'contact.gdprAccepted'],
    control: methods.control,
  });

  const allowSendMail = watchedGdprAccepted && watchedContactMail;

  if (!quoteSettingManagement.get.isSuccess) return <CenteredSpinner />;

  const setting = quoteSettingManagement.get.data.data;
  return (
    <>
      <Col xs={8}>
        <Card>
          <Card.Body>
            <QuoteContactForm settings={setting} />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={4}>
        <Card>
          <Card.Body>
            <Title
              className='mb-3'
              heading='lg'
              weight='bold'
              color='secondary'
            >
              <Trans i18nKey='app.purchase.quote_cart_page.send' />
            </Title>
            <SelectField
              name='validityDays'
              label={
                <Trans i18nKey='app.purchase.quote_cart_page.form.validity' />
              }
              options={Array(30)
                .fill(undefined)
                .map((_, i) => ({
                  label: t('app.purchase.quote_cart_page.form.validity_day', {
                    count: i + 1,
                  }),
                  value: i + 1,
                }))}
              required
            />

            <TextField
              label={<Trans i18nKey='app.common.comment' />}
              name='comment'
              type='text'
              as='textarea'
              placeholder={t('app.purchase.quote_cart_page.form.comment')}
              rows={6}
            />
            <Row>
              <Col>
                <CheckboxField
                  name='contact.gdprAccepted'
                  label={
                    setting.resolvedGdpr1 ||
                    t('app.purchase.quote_cart_page.contact.accept_data')
                  }
                />
              </Col>
            </Row>
            <div className='space-y-4 mt-3'>
              <Button
                id={SAVE_AND_SEND_BUTTON_ID}
                variant='primary'
                type='submit'
                fullWidth
                disabled={!allowSendMail}
                isLoading={mutationState.at(-1)?.status === 'pending'}
              >
                <Trans i18nKey='app.purchase.quote_cart_page.form.save_and_send' />
              </Button>
              <Button
                id='save'
                variant='tertiary-blue'
                type='submit'
                fullWidth
                isLoading={mutationState.at(-1)?.status === 'pending'}
              >
                <Trans i18nKey='app.purchase.quote_cart_page.form.save' />
              </Button>
              <Button
                id={PRINT_BUTTON_ID}
                variant='tertiary-blue'
                type='submit'
                fullWidth
                isLoading={mutationState.at(-1)?.status === 'pending'}
              >
                <Trans i18nKey='app.purchase.quote_cart_page.form.print' />
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </>
  );
};

export default QuoteCartContacts;
